import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { formatDate } from '../../helpers/UtilsFunctions';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const createProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic;
  }
  return getProfileCompanyPicUrl(profilePic);
};

const EmployeeDetails = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId } = useParams();

  const [employee, setEmployee] = useState(() => location.state?.employee || null);
  const [isLoading, setIsLoading] = useState(!location.state?.employee);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadEmployee = async () => {
      if (employee) return;
      try {
        setIsLoading(true);
        setError('');
        const response = await dataAPI.getAllEmployees({ showLoading: false });
        const match = (response.data || []).find(emp => String(emp.id) === String(employeeId));
        if (match) {
          setEmployee(match);
        } else {
          setError('Employee not found.');
        }
      } catch (err) {
        console.error('Error loading employee details:', err);
        setError('Unable to load employee details.');
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployee();
  }, [employee, employeeId]);

  const profilePicUrl = useMemo(() => createProfilePicUrl(employee?.profile_pic), [employee]);

  const handleOpenDialog = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles(prev => {
      const existingKeys = new Set(prev.map(file => `${file.name}-${file.size}-${file.lastModified}`));
      const newFiles = files.filter(file => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`));
      return [...prev, ...newFiles];
    });

    // Reset the input value so the same file can be re-selected if needed
    event.target.value = '';
  };

  const handleRemoveSelectedFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const getDocumentId = (doc) =>
    doc?.raw?.id ||
    doc?.raw?.document_id ||
    doc?.raw?.uuid ||
    doc?.id;

  const getDocumentExtension = (doc) => {
    const sources = [
      doc?.raw?.file_path,
      doc?.raw?.document_path,
      doc?.raw?.path,
      doc?.raw?.file_url,
      doc?.raw?.document_url,
      doc?.raw?.original_name,
      doc?.raw?.file_name,
      doc?.raw?.document_name,
      doc?.raw?.name,
      doc?.name
    ];

    for (const source of sources) {
      if (!source) continue;
      const cleaned = String(source).toLowerCase().split('?')[0];
      const match = cleaned.match(/\.([a-z0-9]+)$/);
      if (match) return match[1];
    }

    const mime = doc?.raw?.mime_type || doc?.raw?.content_type;
    if (mime) {
      const [type, subtype] = mime.split('/');
      if (subtype) return subtype;
      if (type) return type;
    }

    return '';
  };

  const getDocumentIcon = (doc) => {
    const extension = getDocumentExtension(doc);
    const ext = extension.toLowerCase();

    const iconProps = {
      fontSize: 'small',
      sx: {
        color:
          ext === 'pdf'
            ? (mode === 'dark' ? '#ff8a8a' : '#e53935')
            : ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)
            ? (mode === 'dark' ? '#66fffa' : '#009391')
            : (mode === 'dark' ? '#a0aec0' : '#7f8c8d')
      }
    };

    if (ext === 'pdf') {
      return <PictureAsPdfIcon {...iconProps} />;
    }

    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'heic'].includes(ext)) {
      return <ImageIcon {...iconProps} />;
    }

    return <InsertDriveFileIcon {...iconProps} />;
  };

  const extractDocuments = (response) => {
    if (!response) return [];

    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.documents)) return response.documents;
    if (Array.isArray(response?.data?.documents)) return response.data.documents;

    return [];
  };

  const normalizeDocument = (doc, index) => {
    const derivedName =
      doc?.original_name ||
      doc?.file_name ||
      doc?.document_name ||
      doc?.name ||
      `Document ${index + 1}`;
    const derivedId = doc?.id || doc?.document_id || doc?.uuid || `${derivedName}-${index}`;
    const uploadedAt =
      doc?.created_at ||
      doc?.uploaded_at ||
      doc?.updated_at ||
      doc?.created_on ||
      doc?.createdAt ||
      new Date().toISOString();

    return {
      id: derivedId,
      name: derivedName,
      uploadedAt,
      raw: doc
    };
  };

  const fetchEmployeeDocuments = useCallback(async () => {
    if (!employeeId) return;

    try {
      setIsDocumentsLoading(true);
      setDocumentsError('');

      const response = await dataAPI.getEmployeeDocuments(employeeId, { showLoading: false });
      const docs = extractDocuments(response).map((item, index) => normalizeDocument(item, index));
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching employee documents:', error);
      setDocumentsError('Unable to load documents right now.');
    } finally {
      setIsDocumentsLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployeeDocuments();
  }, [fetchEmployeeDocuments]);

  const handleUploadDocuments = async () => {
    if (selectedFiles.length === 0) {
      handleCloseDialog();
      return;
    }

    if (!employeeId) return;

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('documents', file);
    });

    setIsUploading(true);
    try {
      await dataAPI.uploadEmployeeDocuments(employeeId, formData);
      await fetchEmployeeDocuments();
      handleCloseDialog();
    } catch (error) {
      console.error('Error uploading employee documents:', error);
      alert('Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    if (!employeeId) return;

    const documentId = getDocumentId(doc);

    if (!documentId) {
      console.warn('Unable to determine document ID for download:', doc);
      alert('Unable to download this document right now.');
      return;
    }

    try {
      const response = await dataAPI.downloadEmployeeDocument(employeeId, documentId, { showLoading: false });
      const downloadUrl =
        response?.data?.download_url ||
        response?.download_url ||
        response?.data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        console.warn('Download URL missing in response:', response);
        alert('Download link is not available at the moment.');
      }
    } catch (error) {
      console.error('Error downloading employee document:', error);
      alert('Failed to download the document. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!employeeId) return;

    const documentId = getDocumentId(doc);

    if (!documentId) {
      console.warn('Unable to determine document ID for deletion:', doc);
      alert('Unable to delete this document right now.');
      return;
    }

    const shouldDelete = window.confirm('Are you sure you want to delete this document?');
    if (!shouldDelete) return;

    try {
      await dataAPI.deleteEmployeeDocument(employeeId, documentId, { showLoading: false });
      setDocuments(prev => prev.filter(item => getDocumentId(item) !== documentId));
    } catch (error) {
      console.error('Error deleting employee document:', error);
      alert(
        error?.message ||
        'Failed to delete the document. It might require you to sign in again if the session has expired.'
      );
    }
  };

  const containerStyle = {
    padding: '24px',
    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
    minHeight: '100vh',
    fontFamily: 'Urbanist, Arial, sans-serif',
    color: mode === 'dark' ? '#ffffff' : '#2c3e50'
  };

  const cardStyle = {
    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
    border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.35)' : '0 6px 18px rgba(0,0,0,0.08)'
  };

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: '12px',
              border: `1px solid ${mode === 'dark' ? '#333333' : '#26C3C0'}`,
              color: mode === 'dark' ? '#ffffff' : '#26C3C0'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <span>Loading employee details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: '12px',
              border: `1px solid ${mode === 'dark' ? '#333333' : '#26C3C0'}`,
              color: mode === 'dark' ? '#ffffff' : '#26C3C0'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <span style={{ fontSize: '18px', fontWeight: 600 }}>Employee Details</span>
        </div>
        <div style={cardStyle}>
          <div style={{
            color: mode === 'dark' ? '#f87171' : '#c0392b',
            fontSize: '16px'
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: '12px',
              border: `1px solid ${mode === 'dark' ? '#333333' : '#26C3C0'}`,
              color: mode === 'dark' ? '#ffffff' : '#26C3C0'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <span style={{ fontSize: '18px', fontWeight: 600 }}>Employee Details</span>
        </div>
        <div style={cardStyle}>
          <div>No employee data available.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${mode === 'dark' ? '#333333' : '#26C3C0'}`,
            color: mode === 'dark' ? '#ffffff' : '#26C3C0'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <span style={{ fontSize: '22px', fontWeight: 700 }}>Employee Details</span>
      </div>

      {/* Employee Profile Card */}
      <div style={{
        ...cardStyle,
        display: 'flex',
        alignItems: 'center',
        gap: '32px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          width: '160px',
          height: '160px',
          borderRadius: '50%',
          overflow: 'hidden',
          border: `4px solid ${mode === 'dark' ? '#1f1f1f' : '#f0f0f0'}`,
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {profilePicUrl ? (
            <img
              src={profilePicUrl}
              alt={employee.full_name || employee.username}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: '48px', fontWeight: '600', color: '#26C3C0' }}>
              {employee.full_name?.[0] || employee.username?.[0] || 'U'}
            </span>
          )}
        </div>

        <div style={{ flex: 1, minWidth: '240px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px' }}>
            {employee.full_name || employee.username || 'Employee'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '18px' }}>
            <div>
              <div style={{ fontSize: '14px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>Username</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{employee.username || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: '14px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>Full Name</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{employee.full_name || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: '14px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>Email</div>
              <div style={{ fontSize: '18px', fontWeight: 600, wordBreak: 'break-word' }}>{employee.email || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: '14px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>Role</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{employee.role_name || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: '14px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>Status</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: employee.is_active ? '#26C3C0' : (mode === 'dark' ? '#f87171' : '#e74c3c') }}>
                {employee.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '14px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>Birthdate</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                {employee.birthdate ? formatDate(employee.birthdate) : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Documents</h2>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenDialog}
            sx={{
              backgroundColor: '#009391',
              borderRadius: '10px',
              textTransform: 'none',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.3px',
              '&:hover': {
                backgroundColor: '#007b79'
              }
            }}
          >
            Add Documents
          </Button>
        </div>

        {documentsError ? (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: mode === 'dark' ? '#f87171' : '#c0392b',
            fontWeight: 600
          }}>
            {documentsError}
          </div>
        ) : isDocumentsLoading ? (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: mode === 'dark' ? '#a0aec0' : '#7f8c8d'
          }}>
            Loading documents...
          </div>
        ) : documents.length === 0 ? (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: mode === 'dark' ? '#a0aec0' : '#7f8c8d'
          }}>
            No documents uploaded yet.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '320px',
              overflowY: 'auto',
              paddingRight: '4px'
            }}
          >
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(180px, 1fr) 160px auto',
                  alignItems: 'center',
                  padding: '8px 14px',
                  border: `1px solid ${mode === 'dark' ? '#2d2d2d' : '#eaeaea'}`,
                  borderRadius: '12px',
                  backgroundColor: mode === 'dark' ? '#121212' : '#f9fbfb',
                  minHeight: '48px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: mode === 'dark' ? '#FFFFFF' : '#2c3e50'
                  }}
                >
                  {getDocumentIcon(doc)}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.name}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: mode === 'dark' ? '#a0aec0' : '#7f8c8d' }}>
                  {`Uploaded on ${formatDate(doc.uploadedAt)}`}
                </div>
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <IconButton
                    onClick={() => handleDownloadDocument(doc)}
                    sx={{
                      color: mode === 'dark' ? '#66fffa' : '#009391'
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteDocument(doc)}
                    sx={{
                      color: mode === 'dark' ? '#ff8a8a' : '#ff5a5a'
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: mode === 'dark' ? '#111111' : '#ffffff',
            color: mode === 'dark' ? '#ffffff' : '#2c3e50',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px' }}>Upload Documents</DialogTitle>
        <DialogContent dividers sx={{ borderColor: mode === 'dark' ? '#1f1f1f' : '#efefef' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '12px',
              flexWrap: 'wrap'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              variant="contained"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                backgroundColor: '#009391',
                borderRadius: '10px',
                textTransform: 'none',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '14px',
                letterSpacing: '0.3px',
                padding: '8px 18px',
                '&:hover': {
                  backgroundColor: '#007b79'
                }
              }}
            >
              Choose Files
            </Button>
            <span
              style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#a0aec0' : '#7f8c8d'
              }}
            >
              {selectedFiles.length > 0
                ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected`
                : 'No file chosen'}
            </span>
          </div>
          {selectedFiles.length > 0 && (
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxHeight: '240px',
                overflowY: 'auto',
                paddingRight: '4px'
              }}
            >
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    border: `1px solid ${mode === 'dark' ? '#2d2d2d' : '#e0e0e0'}`,
                    backgroundColor: mode === 'dark' ? '#121212' : '#f8fbfb',
                    minHeight: '44px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '12px',
                      overflow: 'hidden',
                      flex: 1
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: mode === 'dark' ? '#FFFFFF' : '#2c3e50',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        color: mode === 'dark' ? '#a0aec0' : '#7f8c8d',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <IconButton
                    aria-label={`Remove ${file.name}`}
                    onClick={() => handleRemoveSelectedFile(index)}
                    sx={{
                      color: mode === 'dark' ? '#f87171' : '#ff5a5a'
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: `1px solid ${mode === 'dark' ? '#1f1f1f' : '#efefef'}` }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', color: mode === 'dark' ? '#ffffff' : '#2c3e50' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadDocuments}
            variant="contained"
            disabled={selectedFiles.length === 0 || isUploading}
            sx={{
              backgroundColor: '#009391',
              borderRadius: '10px',
              textTransform: 'none',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.3px',
              '&:hover': {
                backgroundColor: '#007b79'
              }
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmployeeDetails;

